// One row per PERSON, not one row per grant.
//
// The permissions API is a flat list of (email, area, access) grants, which is the right
// shape for the database and the wrong shape for the table: someone holding five
// areas appeared as five rows, so "who can reach the console" could not be
// counted by looking, and changing one person's access meant reading five rows,
// revoking from some of them, and retyping their address into the grant form to
// add anything. Grouping happens here, in a pure module, so the page renders the
// grouped view and the tests pin the grouping without a DOM.
//
// Three rules the grouping keeps:
//   * AREAS ARE ORDERED BY THE NAV, never by arrival. A person's chips read in
//     the same order in every row and in the editor, so scanning down the column
//     compares like with like.
//   * AN AREA TRAVELS WITH ITS LEVEL. "Holds Explorer" is not a fact on its own
//     any more — read-only and full access are different grants of it, and every
//     shape here carries the pair so a caller cannot render or diff one without
//     the other.
//   * PROVENANCE IS PER AREA and is kept, not summarised away. "Who granted this"
//     is the question an access audit asks, and after grouping it has several
//     answers per person; the editor shows the one that belongs to each area.
//   * TIME COMPARES AS TIME. Grant timestamps arrive as ISO strings whose offset
//     the backend picks; comparing them as text would order "+02:00" against "Z"
//     by their spelling.
import type { Grant } from "./api";
import {
  ACCESS_LEVELS,
  ACCESS_META,
  AREAS,
  AREA_LIST,
  type Access,
  type Area,
  type AreaAccess,
} from "./areas";

/** How a grant with no recorded grantor prints, filters, and searches. Seeded
 *  and migrated rows carry a NULL `granted_by`, and that is a class of row an
 *  audit isolates rather than a blank it skips. It is a literal filter value
 *  rather than the filter layer's no-value bucket because a person can hold a
 *  mix: three areas from a named admin and one from the migration, and both
 *  facts have to stay selectable for that one row. Grantors are email addresses,
 *  so this string can never collide with a real one. */
export const NO_GRANTOR = "Not recorded";

export interface GrantedPerson {
  /** Lowercased address. The identity of the row and the key of every write. */
  email: string;
  /** Areas held with the level held on each, in nav order. */
  areas: AreaAccess[];
  /** Distinct named grantors, alphabetical. Empty when every grant is seeded. */
  grantedBy: string[];
  /** Did at least one of this person's grants arrive without a grantor? */
  hasUnrecordedGrantor: boolean;
  /** Oldest / newest grant timestamp held, ISO, or null when none is recorded. */
  firstGrantedAt: string | null;
  lastGrantedAt: string | null;
  /** The originating grant per area, for the editor's provenance line. */
  grantByArea: Partial<Record<Area, Grant>>;
}

/** Milliseconds for an ISO stamp, or null when absent or unparseable. */
function timeOf(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

function byText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

/**
 * Fold the flat grant list into one row per person, ordered by email.
 *
 * Emails are lowercased defensively: the backend already normalizes them, and a
 * row that slipped through in mixed case must still land on the same person
 * rather than opening a second row nobody can reconcile.
 */
export function groupGrantsByPerson(grants: readonly Grant[]): GrantedPerson[] {
  const buckets = new Map<string, Grant[]>();
  for (const grant of grants) {
    const email = grant.email.trim().toLowerCase();
    if (email === "") continue;
    const bucket = buckets.get(email);
    if (bucket) bucket.push(grant);
    else buckets.set(email, [grant]);
  }

  const people: GrantedPerson[] = [];
  for (const [email, rows] of buckets) {
    const held = new Map<Area, Access>();
    const grantByArea: Partial<Record<Area, Grant>> = {};
    const grantors = new Set<string>();
    let hasUnrecordedGrantor = false;
    let first: string | null = null;
    let last: string | null = null;
    let firstAt = Infinity;
    let lastAt = -Infinity;

    for (const row of rows) {
      held.set(row.area, row.access);
      // (email, area) is unique in the table, so this never overwrites a
      // different grant for the same area.
      grantByArea[row.area] = row;
      if (row.grantedBy) grantors.add(row.grantedBy);
      else hasUnrecordedGrantor = true;

      const at = timeOf(row.createdAt);
      if (at === null) continue;
      if (at < firstAt) {
        firstAt = at;
        first = row.createdAt;
      }
      if (at > lastAt) {
        lastAt = at;
        last = row.createdAt;
      }
    }

    people.push({
      email,
      areas: AREAS.filter((a) => held.has(a)).map((area) => ({
        area,
        access: held.get(area) as Access,
      })),
      grantedBy: [...grantors].sort(byText),
      hasUnrecordedGrantor,
      firstGrantedAt: first,
      lastGrantedAt: last,
      grantByArea,
    });
  }

  return people.sort((a, b) => byText(a.email, b.email));
}

/** Everyone who granted this person something, as the row prints and filters it:
 *  the named admins, then `NO_GRANTOR` when any grant came without one. */
export function grantorLabels(person: GrantedPerson): string[] {
  return person.hasUnrecordedGrantor ? [...person.grantedBy, NO_GRANTOR] : [...person.grantedBy];
}

export interface AreaChanges {
  /** Areas newly granted, with the level to grant them at. */
  added: AreaAccess[];
  /** Areas the person keeps, at a DIFFERENT level than before. A move, not a
   *  revoke-and-regrant: the backend upserts, so nobody is briefly left holding
   *  nothing while the console changes its mind about them. */
  changed: AreaAccess[];
  /** Areas taken away entirely. */
  removed: Area[];
}

/** What saving `next` over `current` would do, every list in nav order. */
export function areaChanges(
  current: readonly AreaAccess[],
  next: readonly AreaAccess[],
): AreaChanges {
  const held = new Map(current.map((g) => [g.area, g.access]));
  const wanted = new Map(next.map((g) => [g.area, g.access]));
  const at = (source: Map<Area, Access>) => (area: Area) => ({
    area,
    access: source.get(area) as Access,
  });
  return {
    added: AREAS.filter((a) => wanted.has(a) && !held.has(a)).map(at(wanted)),
    changed: AREAS.filter((a) => wanted.has(a) && held.has(a) && wanted.get(a) !== held.get(a)).map(
      at(wanted),
    ),
    removed: AREAS.filter((a) => held.has(a) && !wanted.has(a)),
  };
}

export function hasAreaChanges(changes: AreaChanges): boolean {
  return changes.added.length > 0 || changes.changed.length > 0 || changes.removed.length > 0;
}

/** The area that authorises this page's OWN writes. Both routes the editor calls
 *  (`POST /permissions`, `DELETE /permissions/{email}/{area}`) are `system_admin`
 *  at `full`, so an operation that takes that away from the caller is the one
 *  operation that can strip their ability to finish the save. */
const SELF_AUTHORITY_AREA: Area = "system_admin";

/** The (area, level) pairs a save must WRITE — the adds and the level moves,
 *  which are the same call to the backend (an upsert). Grouped by level, because
 *  one grant request carries one level, so a nine-area save is at most two calls
 *  rather than nine.
 *
 *  STRONGEST LEVEL FIRST, and that ordering is load-bearing, not cosmetic: a
 *  grant at `read` can only ever REDUCE what the person already had, and if that
 *  person is the caller demoting their own `system_admin`, every later call in
 *  the save 403s. Doing the `full` call first means the only operation that can
 *  cost the caller their authority is the last grant issued. (`saveSequence`
 *  takes it further for the editor, which also has revokes to place.)
 *
 *  Areas come out in nav order, so the calls a save makes are the same every time
 *  it is made. */
export function grantsToWrite(changes: AreaChanges): { access: Access; areas: Area[] }[] {
  const wanted = new Map<Area, Access>(
    [...changes.added, ...changes.changed].map((g) => [g.area, g.access]),
  );
  return [...ACCESS_LEVELS]
    .reverse()
    .map((access) => ({
      access,
      areas: AREAS.filter((a) => wanted.get(a) === access),
    }))
    .filter((call) => call.areas.length > 0);
}

/** One backend call in a save, in the order it must be issued. */
export type SaveOp =
  | { kind: "grant"; access: Access; areas: Area[] }
  | { kind: "revoke"; area: Area };

/**
 * The whole save, as an ordered list of calls.
 *
 * Grants before revokes, so a level move never passes through "holds nothing".
 * The rule on top of that: EVERY operation touching `system_admin` goes last,
 * after every other grant and every other revoke.
 *
 * Why it has to be explicit. This page's own writes are `system_admin` at `full`,
 * so the moment a save takes that from the caller, the rest of that save 403s —
 * a half-applied edit with an error toast, on the one page that cannot retry it
 * for them. The old revoke-only editor got this right by accident: revokes ran in
 * nav order and `system_admin` sorts last. Adding levels broke the accident,
 * because a demotion is a GRANT and grants ran first. So it is a rule now.
 *
 * Deferred for EVERY row, not just the caller's own. Ordering costs nothing on a
 * colleague's row, and making it unconditional means the sequence does not depend
 * on an identity the page may not have resolved yet — the same three-state
 * problem `saveConfirmation` has to reason about, avoided rather than repeated.
 */
export function saveSequence(changes: AreaChanges): SaveOp[] {
  const isAuthority = (area: Area) => area === SELF_AUTHORITY_AREA;
  const split = (keep: (area: Area) => boolean): AreaChanges => ({
    added: changes.added.filter((g) => keep(g.area)),
    changed: changes.changed.filter((g) => keep(g.area)),
    removed: changes.removed.filter(keep),
  });
  const ops = (part: AreaChanges): SaveOp[] => [
    ...grantsToWrite(part).map(({ access, areas }) => ({ kind: "grant" as const, access, areas })),
    ...part.removed.map((area) => ({ kind: "revoke" as const, area })),
  ];
  return [...ops(split((a) => !isAuthority(a))), ...ops(split(isAuthority))];
}

function areaCount(n: number): string {
  return `${n} ${n === 1 ? "area" : "areas"}`;
}

/** Join clauses so only the first is capitalized: "Adding 2 areas, changing 1
 *  area". */
function joinClauses(parts: readonly string[]): string {
  return parts
    .map((part, i) => (i === 0 ? part : part.charAt(0).toLowerCase() + part.slice(1)))
    .join(", ");
}

/** The editor's pending line: "Adding 2 areas, changing 1 area". Empty string
 *  when nothing is pending, so the caller can fall back to its idle hint. */
export function summarizeAreaChanges(changes: AreaChanges): string {
  const parts: string[] = [];
  if (changes.added.length > 0) parts.push(`Adding ${areaCount(changes.added.length)}`);
  if (changes.changed.length > 0) parts.push(`Changing ${areaCount(changes.changed.length)}`);
  if (changes.removed.length > 0) parts.push(`Removing ${areaCount(changes.removed.length)}`);
  return joinClauses(parts);
}

/** A save the operator has to acknowledge before it runs. */
export interface SaveConfirmation {
  title: string;
  message: string;
  confirmLabel: string;
}

/**
 * Does this save need a confirmation, and what does it say?
 *
 * Only two saves earn a dialog: emptying someone out entirely, and losing the
 * WRITE half of `system_admin` on YOUR OWN row, which is the one change this page
 * cannot undo for you. Everything else applies on click and is one click to put
 * back.
 *
 * Losing it means revoked OR dropped to Read only, and the second is the one a
 * reader forgets: a read-only System Admin still sees this page and every grant
 * on it, and cannot change a single one — including cannot give themselves back
 * the level that would. It reads as a smaller change than a revoke and locks the
 * same door.
 *
 * `isSelf` is deliberately three-state. `null` means identity is NOT YET KNOWN
 * (the `/me` query still in flight, or resolved without an email), and unknown
 * counts as possibly-self: an unnecessary dialog on a colleague's row costs one
 * click, while a skipped one on your own row costs you the page and needs
 * another System Admin to undo. Treating unknown as "not me" is the bug this
 * parameter exists to prevent.
 */
export function saveConfirmation(
  person: GrantedPerson,
  next: readonly AreaAccess[],
  changes: AreaChanges,
  isSelf: boolean | null,
): SaveConfirmation | null {
  const revokesSystemAdmin = changes.removed.includes("system_admin");
  const demotesSystemAdmin = changes.changed.some(
    (g) => g.area === "system_admin" && g.access === "read",
  );
  const losesSystemAdmin = revokesSystemAdmin || demotesSystemAdmin;
  const maybeSelf = isSelf !== false;

  if (next.length === 0 && person.areas.length > 0) {
    // The COUNT, not "every area": what this page can revoke is what it can
    // see, and `fetchPermissions` drops a grant naming an area newer than this
    // bundle (`parseAreas`, deliberately, so an ahead-of-us backend degrades
    // rather than crashes). Naming the number keeps the promise equal to the
    // action in the window where a deployed console is behind the backend.
    const held = areaCount(person.areas.length);
    const base = `This removes all ${held} from ${person.email}. They stay a signed in employee, but the console shows them nothing.`;
    return {
      title: "Remove all access?",
      message:
        isSelf === true
          ? `This removes all ${held} from ${person.email}, which is you. The console will show you nothing, and only another System Admin can give it back.`
          : maybeSelf && losesSystemAdmin
            ? `${base} If that is your own account, only another System Admin can give it back.`
            : base,
      confirmLabel: "Remove all access",
    };
  }

  if (losesSystemAdmin && maybeSelf) {
    // Two different losses, so two different sentences. "Dropping to Read only"
    // has to say what read-only System Admin still does, or it reads as a
    // no-op right up until the first save is refused.
    const what = demotesSystemAdmin
      ? `${ACCESS_META.read.label} System Admin can see who holds what and change none of it`
      : "System Admin is what lets you manage permissions";
    const verb = demotesSystemAdmin ? "Dropping" : "Removing";
    return {
      title: demotesSystemAdmin
        ? isSelf === true
          ? "Drop your own System Admin to Read only?"
          : "Drop System Admin to Read only?"
        : isSelf === true
          ? "Remove your own System Admin?"
          : "Remove System Admin?",
      message:
        isSelf === true
          ? `${what}. ${verb} it on ${person.email}, which is you, closes this page to changes and only another System Admin can give it back.`
          : `${what}. If ${person.email} is your own account, ${verb.toLowerCase()} it closes this page to changes, and only another System Admin can give it back.`,
      confirmLabel: demotesSystemAdmin ? "Drop to Read only" : "Remove it",
    };
  }

  return null;
}

/** What a completed save says in its toast. Same wording as the pending line,
 *  in the past tense. */
export function describeAreaChanges(changes: AreaChanges): string {
  const parts: string[] = [];
  if (changes.added.length > 0) parts.push(`Added ${areaCount(changes.added.length)}`);
  if (changes.changed.length > 0) parts.push(`Changed ${areaCount(changes.changed.length)}`);
  if (changes.removed.length > 0) parts.push(`Removed ${areaCount(changes.removed.length)}`);
  return joinClauses(parts);
}

// ── the editor's draft state ────────────────────────────────────────────────
//
// The Permissions page holds an in-progress edit as a Map<Area, Access>. These
// three turn a click into the next draft, and they live here rather than in the
// page because they are the part with rules in it: what a re-tick restores, and
// what order a draft becomes grants in.

/** Tick / untick one area. Unticking drops it; re-ticking restores the level it
 *  last had — the one they hold, or the one picked earlier in this session —
 *  falling back to `fallback` — normally the area's default (Full access where
 *  that means something, which is what a grant meant before levels existed, so
 *  the common case stays one click; the area's only level where it does not),
 *  and always Full access while the backend cannot honour a level at all. The
 *  caller supplies it (`newGrantLevel`) so this stays a pure map operation.
 *
 *  `remembered` is what makes that true, and it is not a nicety: without it an
 *  untick erased the level, so untick-then-retick on somebody held at Read only
 *  silently proposed promoting them to Full access. Visible in the summary as
 *  "Changing 1 area", but nobody asked for it and the surprise ran in the
 *  permissive direction. */
export function toggled(
  prev: ReadonlyMap<Area, Access>,
  area: Area,
  remembered: ReadonlyMap<Area, Access>,
  fallback: Access,
): Map<Area, Access> {
  const next = new Map(prev);
  if (next.has(area)) next.delete(area);
  else next.set(area, remembered.get(area) ?? fallback);
  return next;
}

export function levelled(
  prev: ReadonlyMap<Area, Access>,
  area: Area,
  access: Access,
): Map<Area, Access> {
  const next = new Map(prev);
  next.set(area, access);
  return next;
}

/** The editor and the form both hold their draft as a Map and both write it as a
 *  list; this is the one conversion. */
export function asGrants(selected: ReadonlyMap<Area, Access>): AreaAccess[] {
  return AREA_LIST.filter((a) => selected.has(a.key)).map((a) => ({
    area: a.key,
    access: selected.get(a.key) as Access,
  }));
}
